import { useState, useCallback } from 'react';
import { authApi } from '@/services/api';

export interface Session {
  id: string;
  ip: string;
  userAgent: string;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

export function useSessions() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.getSessions();
      if (response?.success && response.data) {
        setSessions(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch sessions'));
    } finally {
      setLoading(false);
    }
  }, []);

  const revokeSession = useCallback(async (sessionId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.revokeSession(sessionId);
      if (response?.success) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to revoke session'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const revokeAllSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authApi.revokeAllSessions();
      if (response?.success) {
        setSessions([]);
        return true;
      }
      return false;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to revoke all sessions'));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    sessions,
    loading,
    error,
    fetchSessions,
    revokeSession,
    revokeAllSessions,
  };
}
